import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import apiService from '../services/api';

interface WebServicesOutageFormDialogProps {
  open: boolean;
  onClose: () => void;
  outage: any | null;
}

const PRODUCT_OPTIONS = [
  'Domains',
  'Web Hosting',
  'Virtual Machines',
  'Acronis',
  'Pallisade',
  'Sonic Webmail - sonicmail.com.au',
  'Sonic Webmail - sonicmail.net.au',
  'Google Workspace',
  'Microsoft 365',
];

const STATUS_OPTIONS = ['planned', 'active', 'in_progress', 'resolved'];

const SEVERITY_OPTIONS = ['low', 'medium', 'high', 'critical'];

const WebServicesOutageFormDialog: React.FC<WebServicesOutageFormDialogProps> = ({
  open,
  onClose,
  outage,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatingId, setGeneratingId] = useState(false);
  
  const [formData, setFormData] = useState({
    incident_id: '',
    title: '',
    description: '',
    product: '',
    fault_location: '',
    status: 'active',
    severity: 'medium',
    start_time: null as Date | null,
    end_time: null as Date | null,
  });

  useEffect(() => {
    if (open) {
      if (outage) {
        // Edit mode - populate form
        setFormData({
          incident_id: outage.incident_id || '',
          title: outage.title || '',
          description: outage.description || '',
          product: outage.product || '',
          fault_location: outage.fault_location || '',
          status: outage.status || 'active',
          severity: outage.severity || 'medium',
          start_time: outage.start_time ? new Date(outage.start_time) : null,
          end_time: outage.resolved_at ? new Date(outage.resolved_at) : null,
        });
      } else {
        // Create mode - reset form and generate ID
        setFormData({
          incident_id: '',
          title: '',
          description: '',
          product: '',
          fault_location: '',
          status: 'active',
          severity: 'medium',
          start_time: null,
          end_time: null,
        });
        generateIncidentId();
      }
      setError(null);
    }
  }, [open, outage]);

  const generateIncidentId = async () => {
    try {
      setGeneratingId(true);
      const incidentId = await apiService.generateWebServicesIncidentId();
      setFormData(prev => ({ ...prev, incident_id: incidentId }));
    } catch (err: any) {
      setError(`Failed to generate incident ID: ${err.message}`);
    } finally {
      setGeneratingId(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validate required fields
      if (!formData.title.trim()) {
        setError('Title is required');
        return;
      }
      if (!formData.product) {
        setError('Product is required');
        return;
      }
      if (!formData.incident_id) {
        setError('Incident ID is required');
        return;
      }

      const payload: any = {
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        product: formData.product,
        fault_location: formData.fault_location.trim() || null,
        status: formData.status,
        severity: formData.severity,
        start_time: formData.start_time ? formData.start_time.toISOString() : null,
        end_time: formData.end_time ? formData.end_time.toISOString() : null,
      };

      if (outage) {
        // Update existing outage
        await apiService.updateWebServicesOutage(outage.id, payload);
      } else {
        // Create new outage
        payload.incident_id = formData.incident_id;
        await apiService.createWebServicesOutage(payload);
      }

      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save outage');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {outage ? 'Edit Web Services Outage' : 'Create Web Services Outage'}
        </DialogTitle>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Incident ID"
                value={formData.incident_id}
                onChange={(e) => handleChange('incident_id', e.target.value)}
                disabled={outage !== null || generatingId}
                helperText={generatingId ? 'Generating...' : outage ? 'Cannot change incident ID' : 'Auto-generated'}
                required
              />
              {!outage && !generatingId && (
                <Button
                  size="small"
                  onClick={generateIncidentId}
                  sx={{ mt: 0.5 }}
                >
                  Regenerate ID
                </Button>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Product"
                value={formData.product}
                onChange={(e) => handleChange('product', e.target.value)}
                required
                SelectProps={{ native: false }}
              >
                {PRODUCT_OPTIONS.map((product) => (
                  <MenuItem key={product} value={product}>
                    {product}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Fault Location"
                value={formData.fault_location}
                onChange={(e) => handleChange('fault_location', e.target.value)}
                placeholder="e.g. Sydney Data Centre, DNS servers"
                helperText="Where or what is the source of the fault"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                SelectProps={{ native: false }}
              >
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Severity"
                value={formData.severity}
                onChange={(e) => handleChange('severity', e.target.value)}
                SelectProps={{ native: false }}
              >
                {SEVERITY_OPTIONS.map((severity) => (
                  <MenuItem key={severity} value={severity}>
                    {severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Start Time"
                value={formData.start_time}
                onChange={(newValue) => handleChange('start_time', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="End Time"
                value={formData.end_time}
                onChange={(newValue) => handleChange('end_time', newValue)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    helperText: 'Setting end time will auto-update status to Resolved',
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || generatingId}
          >
            {loading ? <CircularProgress size={20} /> : outage ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default WebServicesOutageFormDialog;

